
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaStar, FaRegStar, FaUserCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import AverageRating from './AverageRating';
import ReviewForm from './ReviewForm';
import FeatureRatings from './FeatureRatings';

const ReviewsSection = ({ propertyId }) => {
  const location = useLocation();
  // const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ reviews: [], featureAverages: null });
  const [newReview, setNewReview] = useState({
    rating: 5,
    comment: ''
  });
  const [authState, setAuthState] = useState({
    isLoggedIn: false,
    checkingAuth: false
  });

  // Fetch reviews
  const fetchReviews = async () => {
    try {
      setLoading(true);

      const response = await fetch(
        `https://pp-backend-1-sa8p.onrender.com/properties/${propertyId}/reviews`
      );

      if (!response.ok) throw new Error('Failed to fetch reviews');
      
      const data = await response.json();
      setData({
        reviews: data.reviews,
        featureAverages: data.featureAverages
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    setAuthState({
      isLoggedIn: !!token,
      checkingAuth: false
    });

    if (propertyId) fetchReviews();
  }, []);

  // Submit new review
  const handleSubmit = async (reviewData) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to submit a review');
      setAuthState({ isLoggedIn: false, checkingAuth: false });
      return;
    }

    // Send both review text and parameterized ratings
    const response = await fetch(
      `https://pp-backend-1-sa8p.onrender.com/properties/${propertyId}/reviews`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          review_text: reviewData.review_text,
          parameters: reviewData.parameters,
          rating: reviewData.rating // Optional: for backward compatibility
        }),
      }
    );

    if (response.status === 401) {
      localStorage.removeItem('token');
      setAuthState({ isLoggedIn: false, checkingAuth: false });
      toast.error('Session expired. Please login again.');
      return;
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Submission failed');
    }

    // Clear form
    setNewReview({ 
      rating: 5, 
      comment: '',
      parameters: {
        location_connectivity: 0,
        construction_quality: 0,
        amenities_facilities: 0,
        developer_credibility: 0,
        public_transport: 0,
        community_vibe: 0
      }
    });
    
    toast.success('Review submitted successfully!');
    await fetchReviews();

  } catch (err) {
    toast.error(err.message);
  }
};

  if (loading) return <Loading>Loading reviews...</Loading>;
  if (error) return <Error>Error loading reviews: {error}</Error>;

  return (
    <Container>
      <SectionContainer>
        <MainHeader>Customer Reviews</MainHeader>
        <AlignedRow>
          <AverageRatingBox>
            <AverageRating reviews={data.reviews} />
          </AverageRatingBox>
          <FeatureRatingsBox>
            <FeatureRatings featureAverages={data.featureAverages} />
          </FeatureRatingsBox>
        </AlignedRow>
      </SectionContainer>
      

      {/* Review Form - Only shows when properly authenticated */}
      {authState.isLoggedIn ? (
        <ReviewForm onSubmit={handleSubmit}/>
          
      ) : (
        <LoginPrompt>
          <p>Want to share your experience?</p>
          <Link to="/sign-in" state={{ from: location.pathname }}>
            Login to leave a review
          </Link>
        </LoginPrompt>
      )}

      {/* Reviews List - shows all reviews regardless of auth status */}
      <ReviewsGrid>
        {data.reviews && data.reviews.length > 0 ? (
          data.reviews.map((review) => (
            <ReviewCard key={review._id}>
              <ReviewHeader>
                <UserAvatar>
                  {review.user_avatar ? (
                    <img src={review.user_avatar} alt={review.user_name || 'Anonymous'} />
                  ) : (
                    <FaUserCircle size={32} />
                  )}
                </UserAvatar>
                <div>
                  <UserName>{review.user_name || 'Anonymous'}</UserName>
                  <ReviewDate>
                    {new Date(review.created_at).toLocaleDateString()}
                  </ReviewDate>
                </div>
              </ReviewHeader>
              <Rating>
                {[...Array(5)].map((_, i) => (
                  i < review.rating ? 
                    <FaStar key={i} color="#FFD700" /> : 
                    <FaRegStar key={i} color="#e4e5e9" />
                ))}
              </Rating>
              <ReviewText>{review.review_text}</ReviewText>
            </ReviewCard>
          ))
        ) : (
          <NoReviews>
            No reviews yet. Be the first to review!
          </NoReviews>
        )}
      </ReviewsGrid>
    </Container>
  );
};

// Helper function to calculate average rating
// function calculateAverage(reviews) {
//   if (reviews.length === 0) return 0;
//   const sum = reviews.reduce((total, review) => total + review.rating, 0);
//   return sum / reviews.length;
// }

// Styled Components (keep all your existing styles exactly the same)
const Container = styled.section`
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
`;

// const SectionHeader = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   margin-bottom: 1.5rem;
//   border-bottom: 1px solid #eee;
//   padding-bottom: 1rem;


//   h2 {
//     margin: 0;
//     font-size: 1.5rem;
//   }
// `;
// const AverageRating = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 0.5rem;

//   span {
//     font-size: 0.9rem;
//     color: #666;
//   }

//   svg {
//     color: #FFD700;
//   }
// `;

// const ReviewForm = styled.form`
//   background: #f8f9fa;
//   padding: 1.5rem;
//   border-radius: 8px;
//   margin-bottom: 2rem;
//   box-shadow: 0 1px 3px rgba(0,0,0,0.1);

//   h3 {
//     margin-top: 0;
//     margin-bottom: 1rem;
//     font-size: 1.2rem;
//   }
// `;






const LoginPrompt = styled.div`
  text-align: center;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 2rem;

  p {
    margin: 0 0 0.5rem 0;
    color: #555;
  }

  a {
    color: #3498db;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ReviewCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const UserAvatar = styled.div`
  img, svg {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    color: #7f8c8d;
  }
`;

const UserName = styled.div`
  font-weight: 600;
  color: #333;
`;

const ReviewDate = styled.div`
  font-size: 0.8rem;
  color: #777;
`;

const Rating = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1rem;
`;

const ReviewText = styled.p`
  color: #555;
  line-height: 1.6;
  margin: 0;
`;

const NoReviews = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 2rem;
  color: #777;
  background: #f8f9fa;
  border-radius: 8px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 2rem;
  color: #777;
`;

const Error = styled.div`
  color: #e74c3c;
  padding: 1rem;
  background: #fdecea;
  border-radius: 4px;
  text-align: center;
`;
const SectionContainer = styled.div`
  padding: 1rem;
  background: white;
  border-radius: 8px;
`;

const MainHeader = styled.h3`
  font-size: 1.2rem;
  margin: 0 0 1rem 0;
  color: #333;
`;

// const ContentRow = styled.div`
//   display: flex;
//   align-items: flex-start;
//   gap: 1.5rem;

//   @media (max-width: 768px) {
//     flex-direction: column;
//     gap: 1rem;
//   }
// `;

const AlignedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  margin-top: 1.5rem;
  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
    gap: 1.5rem;
  }
`;

const AverageRatingBox = styled.div`
  width: 240px;
  min-width: 200px;
  max-width: 300px;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
`;

const FeatureRatingsBox = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 320px;
`;


export default ReviewsSection;
